import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { DeviceIcon } from '../ui/DeviceIcon'
import { Drawer } from '../ui/Drawer'

interface SiteClient {
  id: string
  mac: string
  ip: string | null
  hostname: string | null
  display_name: string | null
  oui: string | null
  device_type: string | null
  connection_type: string | null
  switch_mac: string | null
  switch_port: number | null
  switch_name: string | null
  switch_device_id: string | null
  ap_mac: string | null
  ap_name: string | null
  channel: number | null
  radio: string | null
  rssi: number | null
  signal: number | null
  vlan_id: number | null
  network: string | null
  uptime: number | null
  rx_bytes: number | null
  tx_bytes: number | null
  satisfaction: number | null
  is_online: number
  last_seen: string | null
  first_seen: string | null
  icon_url: string | null
  device_category: string | null
}

interface Props {
  siteId: string
  onOpenNetworkDevice?: (deviceId: string, highlightPort?: number) => void
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${(bytes / 1073741824).toFixed(1)} GB`
}

function formatUptime(seconds: number | null): string {
  if (!seconds) return '—'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

function formatRadio(radio: string | null, channel: number | null): string {
  if (!radio) return ''
  const band = radio === 'na' ? '5GHz' : radio === 'ng' ? '2.4GHz' : radio === '6e' ? '6GHz' : radio
  return channel ? `${band} ch${channel}` : band
}

function connectedToLabel(client: SiteClient): string {
  const isWired = client.connection_type === 'wired'
  if (isWired) {
    if (client.switch_port == null) return '—'
    const portLabel = `Port ${client.switch_port}`
    return client.switch_name ? `${client.switch_name} ${portLabel}` : portLabel
  }
  const apLabel = client.ap_name || client.ap_mac || '—'
  const radioInfo = formatRadio(client.radio, client.channel)
  return radioInfo ? `${apLabel} (${radioInfo})` : apLabel
}

export function SiteClientsTab({ siteId, onOpenNetworkDevice }: Props) {
  const [clients, setClients] = useState<SiteClient[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [connectionFilter, setConnectionFilter] = useState<string>('')
  const [onlineOnly, setOnlineOnly] = useState(true)
  const [selectedClient, setSelectedClient] = useState<SiteClient | null>(null)
  const { toast } = useToast()

  const loadClients = useCallback(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (connectionFilter) params.set('connection_type', connectionFilter)
    if (onlineOnly) params.set('is_online', 'true')
    params.set('limit', '200')

    api.get<{ data: SiteClient[]; total: number }>(`/sites/${siteId}/clients?${params}`)
      .then(resp => {
        setClients(resp.data || [])
        setTotal(resp.total || 0)
      })
      .catch(err => toast(`Failed to load clients: ${err?.message}`, 'error'))
      .finally(() => setLoading(false))
  }, [siteId, search, connectionFilter, onlineOnly, toast])

  useEffect(() => { loadClients() }, [loadClients])

  const wiredCount = clients.filter(c => c.connection_type === 'wired').length
  const wirelessCount = clients.filter(c => c.connection_type === 'wireless').length

  if (loading) return <div className="skeleton" style={{ height: 300, borderRadius: 'var(--radius-lg)' }} />

  return (
    <div>
      {/* Sticky tab header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg-0)', paddingBottom: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Connected Clients</h3>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
              {total} total · {wiredCount} wired · {wirelessCount} wireless
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, IP, MAC..."
            className="input"
            style={{ fontSize: 12, padding: '6px 10px', width: 220 }}
          />
          <select
            value={connectionFilter}
            onChange={e => setConnectionFilter(e.target.value)}
            className="input"
            style={{ fontSize: 12, padding: '6px 8px' }}
          >
            <option value="">All types</option>
            <option value="wired">Wired</option>
            <option value="wireless">Wireless</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlineOnly} onChange={e => setOnlineOnly(e.target.checked)} />
            Online only
          </label>
        </div>
      </div>

      <div className="table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 30 }} />
              <th>Name</th>
              <th>IP</th>
              <th style={{ width: 140 }}>MAC</th>
              <th style={{ width: 60 }}>Type</th>
              <th>Connected To</th>
              <th style={{ width: 50 }}>VLAN</th>
              <th style={{ width: 60 }}>Signal</th>
              <th style={{ width: 70 }}>Uptime</th>
              <th style={{ width: 80 }}>Traffic</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => {
              const name = client.display_name || client.hostname || client.oui || client.mac
              const isWired = client.connection_type === 'wired'
              const signalPct = client.rssi != null ? Math.min(100, Math.max(0, (client.rssi + 100) * 2)) : null
              const trafficTotal = (client.rx_bytes || 0) + (client.tx_bytes || 0)

              return (
                <tr
                  key={client.id}
                  style={{ cursor: 'pointer', background: selectedClient?.id === client.id ? 'var(--bg-overlay)' : undefined }}
                  onClick={() => setSelectedClient(selectedClient?.id === client.id ? null : client)}
                >
                  <td>
                    <span style={{ fontSize: 8, color: client.is_online ? 'var(--ok)' : 'var(--text-2)' }}>
                      {client.is_online ? '\u25CF' : '\u25CB'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <DeviceIcon
                      iconUrl={client.icon_url}
                      deviceType={client.device_category || (isWired ? 'workstation' : 'phone')}
                      size={20}
                    />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{name}</span>
                    </div>
                    {client.display_name && client.hostname && client.display_name !== client.hostname && (
                      <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 6 }}>{client.hostname}</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: client.ip ? 'var(--text)' : 'var(--text-tertiary)' }}>
                      {client.ip || '—'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                      {client.mac}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {isWired ? 'Wired' : 'WiFi'}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const label = connectedToLabel(client)
                      const hasDevice = client.connection_type === 'wired'
                        ? !!client.switch_device_id
                        : false  // APs not yet wired to modal
                      if (hasDevice && onOpenNetworkDevice) {
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onOpenNetworkDevice(client.switch_device_id!, client.switch_port ?? undefined)
                            }}
                            style={{
                              fontSize: 12, color: 'var(--accent)', fontWeight: 600,
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 0, fontFamily: 'inherit',
                              textDecoration: 'underline', textDecorationColor: 'var(--accent-m)',
                              textUnderlineOffset: 2,
                            }}
                            title="View switch port detail"
                          >
                            {label}
                          </button>
                        )
                      }
                      return (
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {label}
                        </span>
                      )
                    })()}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                      {client.vlan_id || client.network || '—'}
                    </span>
                  </td>
                  <td>
                    {!isWired && signalPct != null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 30, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            width: `${signalPct}%`, height: '100%', borderRadius: 3,
                            background: signalPct > 60 ? 'var(--ok)' : signalPct > 30 ? 'var(--warn)' : 'var(--err)',
                          }} />
                        </div>
                        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                          {client.rssi}
                        </span>
                      </div>
                    ) : <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                      {formatUptime(client.uptime)}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                      {formatBytes(trafficTotal)}
                    </span>
                  </td>
                </tr>
              )
            })}
            {clients.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)', fontSize: 13 }}>
                No clients found
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Client detail drawer */}
      <Drawer
        isOpen={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        title={selectedClient?.display_name || selectedClient?.hostname || selectedClient?.mac || 'Client'}
      >
        {selectedClient && <ClientDetail client={selectedClient} />}
      </Drawer>
    </div>
  )
}

function ClientDetail({ client }: { client: SiteClient }) {
  const isWired = client.connection_type === 'wired'
  const signalPct = client.rssi != null ? Math.min(100, Math.max(0, (client.rssi + 100) * 2)) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header with icon and status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <DeviceIcon
          iconUrl={client.icon_url}
          deviceType={client.device_category || (isWired ? 'workstation' : 'phone')}
          size={48}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            {client.display_name || client.hostname || client.mac}
          </div>
          {client.display_name && client.hostname && client.display_name !== client.hostname && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{client.hostname}</div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: client.is_online ? 'var(--ok-m)' : 'var(--bg-3)',
              color: client.is_online ? 'var(--ok)' : 'var(--text-2)',
            }}>
              {client.is_online ? 'Online' : 'Offline'}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: isWired ? 'var(--info-m)' : 'rgba(124,58,237,0.08)',
              color: isWired ? 'var(--info)' : 'var(--color-cable)',
            }}>
              {isWired ? 'Wired' : 'Wireless'}
            </span>
          </div>
        </div>
      </div>

      {/* Identity */}
      <Section title="Identity">
        <DetailRow label="IP Address" value={client.ip} mono />
        <DetailRow label="MAC Address" value={client.mac} mono />
        <DetailRow label="Hostname" value={client.hostname} />
        <DetailRow label="Manufacturer" value={client.oui} />
      </Section>

      {/* Connection */}
      <Section title="Connected To">
        {isWired ? (
          <>
            <DetailRow label="Switch Port" value={client.switch_port != null ? `Port ${client.switch_port}` : null} />
            <DetailRow label="Switch MAC" value={client.switch_mac} mono />
          </>
        ) : (
          <>
            <DetailRow label="Access Point" value={client.ap_name || client.ap_mac} />
            <DetailRow label="Radio" value={formatRadio(client.radio, client.channel)} />
            {signalPct != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Signal Strength</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 80, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${signalPct}%`, height: '100%', borderRadius: 4,
                      background: signalPct > 60 ? 'var(--ok)' : signalPct > 30 ? 'var(--warn)' : 'var(--err)',
                    }} />
                  </div>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text)', fontWeight: 600 }}>
                    {client.rssi} dBm
                  </span>
                </div>
              </div>
            )}
          </>
        )}
        <DetailRow label="VLAN" value={client.vlan_id?.toString()} />
        <DetailRow label="Network" value={client.network} />
      </Section>

      {/* Activity */}
      <Section title="Activity">
        <DetailRow label="Uptime" value={formatUptime(client.uptime)} />
        <DetailRow label="Download" value={formatBytes(client.rx_bytes)} />
        <DetailRow label="Upload" value={formatBytes(client.tx_bytes)} />
        {client.satisfaction != null && (
          <DetailRow label="Experience" value={`${client.satisfaction}%`} />
        )}
      </Section>

      {/* Timestamps */}
      <Section title="History">
        <DetailRow label="First Seen" value={client.first_seen ? new Date(client.first_seen).toLocaleString() : null} />
        <DetailRow label="Last Seen" value={client.last_seen ? new Date(client.last_seen).toLocaleString() : null} />
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ background: 'var(--bg-overlay)', borderRadius: 8, padding: '4px 12px' }}>
        {children}
      </div>
    </div>
  )
}

function DetailRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{
        fontSize: 12,
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        color: value && value !== '—' ? 'var(--text)' : 'var(--text-tertiary)',
        fontWeight: 500,
      }}>
        {value || '—'}
      </span>
    </div>
  )
}
